import {Notation} from 'biojs/BioConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {StructuredTableFolderOption} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';

/**
 * This enum specifies which type of Registration table we are inserting.  A Registration table can either:
 * - create new entities which haven't been created before.  The name column is not available in these cases
 *   and is determined by the labeling strategy.
 * - register existing entities
 * - register batches: this is a legacy mode for creating batches which is only supported using the
 *   LegacyRegistrationTable
 *
 * The values of the enum are used as the actual labels for the buttons in the insertion modal.
 *
 * This enum should be kept in sync with benchling/notebook/structured_tables/constants/registration_tables.py
 */
export enum RegistrationTableInsertType {
  CREATE_NEW_ENTITIES = 'Create new entities',
  REGISTER_EXISTING_ENTITIES = 'Register existing entities',
  LEGACY_REGISTER_BATCHES = 'Register batches',
}

/**
 * Metadata interface for registration structured tables.
 *
 * NOTE: Most keys are optional because when we add a new metadata key (without backfilling, which is the
 * usual strategy), existing tables do NOT have it, so we need to handle the case where the metadata key is
 * not present.
 *
 * NOTE: This metadata is persisted with the structured table. Some metadata (ex.
 * `implementedSchemaInterfaces`, `tagSchemaItemType`) are essentially denormalized from the upstream source,
 * like the original schema. This means that if the upstream source changes, it becomes out-of-sync with the
 * persisted metadata values. This can be desirable, as it means the versioned structured table does not
 * change as the original schema changes.
 *
 * Agent keep in sync: benchling/notebook/structured_tables/constants/registration_tables.py
 */
export interface RegistrationStructuredTableMetadata {
  insertType: RegistrationTableInsertType;
  labelingStrategy: RegistryLabelingStrategy;
  /**
   * ONLY contains the implemented schema interfaces needed for Registration Table generation, NOT all. For
   * example, includes Antibody (needed to generate domain columns) but NOT Equipment (not needed since there
   * are no additional system field columns).
   */
  implementedSchemaInterfaces?: Array<ImplementedStaticSchemaInterfaceGQL>;
  /**
   * Persists the tagSchemaItemType so that downstream code like the Adapter can use it.
   *
   * As of 2025-07-22, the Adapter only uses it to gate spreadsheet import. If we support spreadsheet import
   * for all entity types in the future, then we can consider removing this metadata key as it would be
   * unused.
   */
  tagSchemaItemType?: TagSchemaItemType;
  notation?: Notation | null;
  customSyntaxName?: string | null;
  folderOption?: StructuredTableFolderOption;
  /* If folderOption is SPECIFY_FOLDER, then folderId designates the selected folder to create items in.
   * Otherwise it will be undefined. */
  folderId?: string;
}

export interface RegistrationStructuredTableRowMetadata extends RowMetadata {}
